import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { formatApiErrorDetail } from "../lib/api";

export default function Login() {
  const { login } = useAuth();
  const { t, lang, setLang } = useLang();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success(t("welcome_back"));
      nav("/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grain relative flex items-center justify-center px-4">
      <button
        onClick={() => setLang(lang === "fr" ? "en" : "fr")}
        data-testid="language-toggle-auth"
        className="absolute top-6 right-6 text-xs uppercase tracking-widest text-slate-300 px-3 py-1.5 rounded-lg hover:bg-white/5 z-10"
      >
        {lang}
      </button>
      <div className="w-full max-w-md relative z-[2]">
        <div className="flex items-center gap-4 mb-8">
          <div className="w-16 h-16 rounded-2xl overflow-hidden flex items-center justify-center flex-shrink-0" style={{ background: "#0A3F4A" }}>
            <img src="/cen21-logo.webp" alt="Cen21" className="w-full h-full object-contain" style={{ transform: "scale(2.4)" }} />
          </div>
          <div>
            <div className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>{t("app_name")}</div>
            <div className="text-xs text-slate-400">{t("tagline")}</div>
          </div>
        </div>
        <div className="glass rounded-3xl p-8">
          <h1 className="text-2xl font-bold mb-1" style={{ fontFamily: "Outfit" }}>{t("login_title")}</h1>
          <p className="text-sm text-slate-400 mb-6">{t("login")}</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-slate-300">{t("email")}</Label>
              <Input
                id="email"
                data-testid="auth-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 bg-white/5 border-white/10 focus-visible:ring-indigo-500"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-300">{t("password")}</Label>
              <Input
                id="password"
                data-testid="auth-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 bg-white/5 border-white/10 focus-visible:ring-indigo-500"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="auth-login-button"
              className="w-full btn-glow bg-indigo-500 hover:bg-indigo-600 text-white"
            >
              {loading ? "…" : t("login")}
            </Button>
          </form>
          <div className="mt-6 text-sm text-slate-400 text-center space-y-2">
            <div>
              <Link data-testid="link-to-forgot" to="/forgot-password" className="text-slate-300 hover:text-white">{t("forgot_password")}</Link>
            </div>
            <div>
            {t("no_account")}{" "}
            <Link data-testid="link-to-register" to="/register" className="text-indigo-400 hover:text-indigo-300">
              {t("register")}
            </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
